import { useIntl } from 'react-intl';
import { Checkbox, Col, Form, Input, Popover, Row, Select } from 'antd';

import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';

import {
  FilterTypeEnum,
  ListStockItemsQuery,
} from '@/core/domain/stock/StockItems/StockItems.types';

import OrderExtraFilter from '@/components/_common/ExtraFilters/OrderExtraFilter';
import { usePartConfigurationProfile } from '@/core/domain/stock/Configuration/Configuration.hooks';
import { PartItemFieldsEnum } from '@/core/domain/stock/Configuration/Configuration.types';
import ActiveExtraFilter from '@/components/_common/ExtraFilters/ActiveExtraFilter';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import GroupsInfiniteSelectField from '@/components/_common/GroupsInfiniteSelectField';
import { useEffect } from 'react';
import { Info } from 'react-feather';
import { Group } from '@/components/_common';

function PartsExtraFilters({
  queryParams,
  setQueryParams,
  vertical = false,
  inModal = false,
  onMounted,
}: ExtraFiltersProps<ListStockItemsQuery> & {
  vertical?: boolean;
  inModal?: boolean;
}) {
  const intl = useIntl();

  const colProps = vertical
    ? { span: 24 }
    : { span: 24, md: 12, lg: 6, xxl: !inModal ? 4 : undefined };

  const { data, dataUpdatedAt } = usePartConfigurationProfile();

  const showField = (field: PartItemFieldsEnum) => {
    return data?.result.find((profile) => profile.field === field)?.active;
  };

  useEffect(() => {
    if (onMounted) {
      onMounted();
    }
  }, [dataUpdatedAt]);

  return (
    <Row gutter={[16, 16]} key={dataUpdatedAt}>
      <Col {...colProps}>
        <Form.Item noStyle name="filterType" initialValue={FilterTypeEnum.free}>
          <Select
            placeholder="Filtrar por"
            style={{ width: '100%' }}
            options={[
              {
                label: 'Campo - Livre',
                value: FilterTypeEnum.free,
              },
              {
                label: 'Campo - Especifico',
                value: FilterTypeEnum.specific,
              },
            ]}
            onChange={(value) =>
              setQueryParams((prev) => ({
                ...prev,
                filterType: value,
              }))
            }
          />
        </Form.Item>
      </Col>

      {showField(PartItemFieldsEnum.BarCode) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.barcode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, barcode: value }))
            }
            placeholder={intl.formatMessage({ id: 'parts.entity.barCode' })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.OriginalCode) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.sku}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, sku: value }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.originalCode',
            })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.ManufacturingCode) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.manufacturingCode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, manufacturingCode: value }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.manufacturerCode',
            })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.companyCode) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.companyCode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, companyCode: value }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.companyCode',
            })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.Description) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.description}
            onChange={({ target: { value } }) => {
              if (value === '' || value === undefined) {
                setQueryParams((prev) => ({
                  ...prev,
                  prioritizePrefixMatch: false,
                }));
              }

              setQueryParams((prev) => ({ ...prev, description: value }));
            }}
            placeholder={intl.formatMessage({ id: 'parts.entity.description' })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.Manufacturer) && (
        <Col {...colProps}>
          <ManufacturerInfiniteSelectField
            allowClear
            placeholder={intl.formatMessage({
              id: 'parts.entity.manufacturer',
            })}
            queryFilter={{ active: true }}
            value={queryParams?.manufacturer}
            onChange={(value) =>
              setQueryParams((prev) => ({ ...prev, manufacturer: value }))
            }
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.Group) && (
        <Col {...colProps}>
          <GroupsInfiniteSelectField
            allowClear
            value={queryParams?.subGroup}
            onChange={(value) =>
              setQueryParams((prev) => ({ ...prev, subGroup: value }))
            }
            placeholder={intl.formatMessage({ id: 'parts.entity.subGroup' })}
          />
        </Col>
      )}

      <Col {...colProps}>
        <Input
          value={queryParams?.location}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, location: value }))
          }
          placeholder={intl.formatMessage({ id: 'parts.entity.location' })}
        />
      </Col>

      <Col {...colProps}>
        <OrderExtraFilter
          value={queryParams?.order}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              order: value,
            }))
          }
        />
      </Col>

      <Col {...colProps}>
        <ActiveExtraFilter
          value={queryParams?.active}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              active: value as unknown as boolean,
            }))
          }
        />
      </Col>

      <Col {...colProps}>
        <Checkbox
          checked={queryParams?.isPartReplacement}
          onChange={({ target: { checked } }) =>
            setQueryParams((prev) => ({
              ...prev,
              isPartReplacement: checked,
            }))
          }
          style={{ marginTop: 10 }}
        >
          Mostrar peça substituta ao pesquisar?
        </Checkbox>
      </Col>

      <Col span={24} md={12}>
        <Checkbox
          checked={queryParams?.prioritizePrefixMatch}
          onChange={({ target: { checked } }) =>
            setQueryParams((prev) => ({
              ...prev,
              prioritizePrefixMatch: checked,
            }))
          }
          style={{ marginTop: 10 }}
          disabled={!queryParams?.description}
        >
          <Group gap={10}>
            Priorizar busca por descrição
            <Popover
              title="Como os resultados são ordenados"
              placement="right"
              content={
                <div style={{ maxWidth: 320 }}>
                  <p>Os resultados serão exibidos nesta ordem:</p>
                  <ol style={{ paddingLeft: 20 }}>
                    <li>
                      Peças cuja descrição seja{' '}
                      <strong>exatamente igual</strong> ao termo digitado.
                    </li>
                    <li>
                      Peças cuja descrição <strong>inicie</strong> com o termo
                      pesquisado.
                    </li>
                    <li>
                      Demais peças que <strong>contenham</strong> o termo em
                      qualquer parte da descrição.
                    </li>
                  </ol>
                </div>
              }
            >
              <Info size={20} style={{ cursor: 'pointer' }} />
            </Popover>
          </Group>
        </Checkbox>
      </Col>
    </Row>
  );
}

export default PartsExtraFilters;
